import { connectNestConnection, createNestClient } from "../nest/client"
import { revokeActivePushEnrollment } from "../platform/pushEnrollment"
import { revokePublisherDevice } from "../platform/pushPublisher"
import type { ComputerBackend } from "./types"

const ACCESS_TIMEOUT_MS = 10_000

export interface PairedDevice {
    tokenId: string
    displayName: string
    createdAt: string
    current: boolean
}

export interface PairedDeviceRevocationReport {
    status: "done" | "done-with-gaps"
    requested: 2
    succeeded: number
    failed: number
    outcomes: {
        nest: "revoked" | "already-revoked" | "failed"
        push: "revoked" | "already-revoked" | "not-enrolled" | "failed"
    }
    firstErrors: Array<{ system: "nest" | "push"; message: string }>
}

interface PushRevocationOperations {
    revokeActive(nodeId: string, deviceRef: string): ReturnType<typeof revokeActivePushEnrollment>
    revokePublisher(nodeId: string, deviceRef: string): ReturnType<typeof revokePublisherDevice>
}

const pushRevocationOperations: PushRevocationOperations = {
    revokeActive: revokeActivePushEnrollment,
    revokePublisher: revokePublisherDevice,
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function parsePairedDevices(value: unknown, currentTokenId: string | null): PairedDevice[] {
    if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("The computer returned an invalid access-token list")
    const devices: PairedDevice[] = []
    for (const item of value.items) {
        if (!isRecord(item) || item.kind !== "device" || item.revoked === true) continue
        if (typeof item.tokenId !== "string" || !item.tokenId || typeof item.createdAt !== "string" || !item.createdAt) continue
        devices.push({
            tokenId: item.tokenId,
            displayName: typeof item.displayName === "string" && item.displayName.trim() ? item.displayName.trim() : "Paired device",
            createdAt: item.createdAt,
            current: item.tokenId === currentTokenId,
        })
    }
    return devices
}

export async function listPairedDevices(backend: ComputerBackend, nodeId: string): Promise<PairedDevice[]> {
    const connection = await backend.getConnection(nodeId)
    const client = createNestClient()
    try {
        await connectNestConnection(client, connection, { timeoutMs: ACCESS_TIMEOUT_MS })
        const principal = await client.requestMethod("auth.principal.read", {}, { timeoutMs: ACCESS_TIMEOUT_MS })
        const currentTokenId = isRecord(principal) && typeof principal.tokenId === "string" ? principal.tokenId : null
        const page = await client.requestMethod("auth.token.list", { limit: 500 }, { timeoutMs: ACCESS_TIMEOUT_MS })
        return parsePairedDevices(page, currentTokenId)
    } finally {
        client.close()
    }
}

export async function revokePushMembership(
    nodeId: string,
    deviceRef: string,
    operations: PushRevocationOperations = pushRevocationOperations
): Promise<"revoked" | "already-revoked" | "not-enrolled"> {
    const nativeResult = await operations.revokeActive(nodeId, deviceRef)
    if (nativeResult.status !== "not-mobile" && nativeResult.status !== "not-current-device") return nativeResult.status
    const publisherResult = await operations.revokePublisher(nodeId, deviceRef)
    if (publisherResult.status === "not-electron") {
        throw new Error("Push membership can only be revoked by that mobile device or the WildLark desktop app that created its pairing link")
    }
    return publisherResult.status
}

export async function revokePairedDevice(backend: ComputerBackend, nodeId: string, tokenId: string): Promise<PairedDeviceRevocationReport> {
    const revokeNest = async (): Promise<"revoked" | "already-revoked"> => {
        const connection = await backend.getConnection(nodeId)
        const client = createNestClient()
        try {
            await connectNestConnection(client, connection, { timeoutMs: ACCESS_TIMEOUT_MS })
            const result = await client.requestMethod("auth.token.revoke", { tokenId }, { timeoutMs: ACCESS_TIMEOUT_MS })
            return result.revoked ? "revoked" : "already-revoked"
        } finally {
            client.close()
        }
    }

    const [nest, push] = await Promise.all([
        revokeNest().then(
            (status) => ({ status }) as const,
            (error: unknown) => ({ status: "failed" as const, message: error instanceof Error ? error.message : String(error) })
        ),
        revokePushMembership(nodeId, tokenId).then(
            (status) => ({ status }) as const,
            (error: unknown) => ({ status: "failed" as const, message: error instanceof Error ? error.message : String(error) })
        ),
    ])
    const firstErrors: PairedDeviceRevocationReport["firstErrors"] = []
    if (nest.status === "failed") firstErrors.push({ system: "nest", message: nest.message })
    if (push.status === "failed") firstErrors.push({ system: "push", message: push.message })
    const failed = firstErrors.length
    return {
        status: failed > 0 ? "done-with-gaps" : "done",
        requested: 2,
        succeeded: 2 - failed,
        failed,
        outcomes: { nest: nest.status, push: push.status },
        firstErrors,
    }
}
